// Loads a module's screen lazily and enforces its state: locked shows the sales page,
// a role the module doesn't allow sees a plain refusal, everything else renders.
// If a screen's code chunk fails to load (the app was open across a deploy and the old
// chunk names are gone), reload once to pick up the new build instead of hanging.
import { Component, lazy, Suspense, useMemo, type ReactNode } from 'react';
import { Navigate } from 'react-router';
import { getModule, moduleState } from '@tradeview/core';
import { useAuth } from '../auth';
import { reportError } from '../errors';

const RELOAD_KEY = 'tv.chunk.reloaded';
function withReload(loader: () => Promise<{ default: unknown }>) {
  return () => loader().then(m => { try { sessionStorage.removeItem(RELOAD_KEY); } catch { /* fine */ } return m; }).catch((e: unknown) => {
    reportError('chunk-load', e, (e as Error)?.stack);
    let already = false;
    try { already = sessionStorage.getItem(RELOAD_KEY) === '1'; if (!already) sessionStorage.setItem(RELOAD_KEY, '1'); } catch { /* fine */ }
    if (!already) { location.reload(); return new Promise<never>(() => {}); }
    throw e;
  });
}

class ScreenBoundary extends Component<{ children: ReactNode }, { error: Error | null }> {
  state = { error: null as Error | null };
  static getDerivedStateFromError(error: Error) { return { error }; }
  componentDidCatch(error: Error) { reportError('screen', error, error.stack); }
  render() {
    if (!this.state.error) return this.props.children;
    return (
      <div className="card mt-2">
        <b className="font-head font-bold block">This screen couldn't load</b>
        <span className="text-muted text-[13.5px] block mb-3">{this.state.error.message.split('\n')[0]}</span>
        <button className="btn btn-primary" onClick={() => location.reload()}>Try again</button>
      </div>
    );
  }
}

export function ModuleRoute({ moduleId, screen, roles }: { moduleId: string; screen: string; roles?: string[] }) {
  const { session } = useAuth();
  const m = getModule(moduleId);
  const Screen = useMemo(() => {
    const loader = m?.screens[screen];
    return loader ? lazy(withReload(loader) as () => Promise<{ default: React.ComponentType }>) : null;
  }, [m, screen]);
  if (!m || !Screen) return <Navigate to="/modules" replace />;
  const st = moduleState(session?.entitlements ?? null, moduleId);
  if (st === 'locked') return <Navigate to={`/modules/${moduleId}`} replace />;
  const role = session?.role ?? '';
  if (!m.manifest.roles.includes(role) || (roles && !roles.includes(role))) {
    return <div className="card mt-2"><b className="font-head font-bold block">Not for your role</b><span className="text-muted text-[13.5px]">{m.manifest.name} is available to {m.manifest.roles.join(', ')}.</span></div>;
  }
  return (
    <ScreenBoundary key={`${moduleId}:${screen}`}>
      <Suspense fallback={<div className="flex flex-col gap-2.5 pt-2"><div className="skel h-24 rounded-[14px]" /><div className="skel h-16 rounded-[14px]" /></div>}>
        <div className="pt-2"><Screen /></div>
      </Suspense>
    </ScreenBoundary>
  );
}
